import { createSlice, createAction, current, PayloadAction } from "@reduxjs/toolkit";
import { has, isArray } from "lodash";

// Trigger action for saga compatibility (saga watches this)
export const fetchOrganizationTeam = createAction<any>("organizationTeam/trigger");

// External action this slice responds to
import { logoutStart } from "@/store/slices/auth";

// Top level:
// {
//    loading: 0, // qty of active requests (0 when none)
//    data: {}    // data object keyed by org ID
// }

interface OrgTeamEntry {
  loading?: boolean;
  failed?: boolean;
  team?: any[];
}

interface OrganizationTeamState {
  loading?: number;
  data: Record<number, OrgTeamEntry>;
}

const initialState: OrganizationTeamState = {
  data: {},
};

const organizationTeamSlice = createSlice({
  name: "organizationTeam",
  initialState,
  reducers: {
    // Payload must contain: organization_id
    fetchOrganizationTeamStart(state, action: PayloadAction<any>) {
      const orgId = Number(action.payload.organization_id);

      // Add org entry if not yet present.
      if (!has(state.data, orgId)) {
        state.data[orgId] = {};
      }

      // Add/increment loading flag (top-level, not org-specific).
      if (state.loading === undefined) {
        state.loading = 1;
      } else {
        state.loading++;
      }
    },
    fetchOrganizationTeamSuccess(state, action: PayloadAction<any>) {
      if (!action.payload?.data || !action.payload?.meta) {
        console.error(
          "Missing payload property in fetchOrganizationTeamSuccess.",
          action.payload
        );
        return;
      }

      // Extract org ID from the meta.path string.
      // @TODO We'll eventually add `organization_id` as a meta property.
      const path = action.payload.meta.path;
      const matches = path.match(/\/api\/v1\/organizations\/(\d+)\/users/);
      const orgId = Number(matches[1]);

      const team =
        action.payload?.data && isArray(action.payload.data)
          ? action.payload.data
          : [];

      state.loading = (state.loading || 1) - 1;
      state.data[orgId] = {
        failed: false,
        loading: false,
        team,
      };
    },
    fetchOrganizationTeamFailure(state, action: PayloadAction<any>) {
      // Not a lot we can do for a failed request, so log it to the console.
      console.error("fetchOrganizationTeamFailure", current(state), action.payload);

      // @TODO Unset loading, etc
      state.loading = (state.loading || 1) - 1;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutStart, () => ({ ...initialState }));
  },
});

export const {
  fetchOrganizationTeamStart,
  fetchOrganizationTeamSuccess,
  fetchOrganizationTeamFailure,
} = organizationTeamSlice.actions;

export default organizationTeamSlice.reducer;
